import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Calendar, Clock, CheckCircle, XCircle, Loader2, AlertCircle, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function ReservarEspacioPage({ embedded = false }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [reservasAbiertas, setReservasAbiertas] = useState(true);
  const [disponibilidad, setDisponibilidad] = useState([]);
  const [misReservas, setMisReservas] = useState({ futuras: [], pasadas: [] });
  
  // Estado del calendario
  const [mesActual, setMesActual] = useState(new Date());
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [horaSeleccionada, setHoraSeleccionada] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [slotsDelDia, setSlotsDelDia] = useState([]);
  
  // Vista activa
  const [vista, setVista] = useState('calendario'); // 'calendario' | 'mis-reservas'

  useEffect(() => {
    fetchData();
  }, [token, mesActual]);

  const fetchData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Primero obtener info pública del espacio
      const infoRes = await axios.get(`${API_URL}/reservas-espacio/info`);
      setReservasAbiertas(infoRes.data.reservas_abiertas !== false);
      
      // Calcular rango de fechas del mes
      const primerDia = new Date(mesActual.getFullYear(), mesActual.getMonth(), 1);
      const ultimoDia = new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 0);
      
      const fechaDesde = primerDia.toISOString().split('T')[0];
      const fechaHasta = ultimoDia.toISOString().split('T')[0];
      
      const [dispRes, reservasRes] = await Promise.all([
        axios.get(`${API_URL}/reservas-espacio/disponibilidad?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/reservas-espacio/mis-reservas`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setConfig({ ...(dispRes.data.config || {}), ...(infoRes.data || {}) });
      setDisponibilidad(dispRes.data.disponibilidad);
      setMisReservas(reservasRes.data);
    } catch (err) {
      console.error('Error cargando datos:', err);
      toast.error('Error al cargar la disponibilidad');
    } finally {
      setLoading(false);
    }
  };

  const handleFechaClick = (fecha) => {
    setFechaSeleccionada(fecha);
    setHoraSeleccionada(null);
    
    // Buscar slots de ese día
    const diaInfo = disponibilidad.find(d => d.fecha === fecha);
    setSlotsDelDia(diaInfo?.slots || []);
  };

  const handleReservar = async () => {
    console.log('handleReservar called', { fechaSeleccionada, horaSeleccionada, token: !!token });
    
    if (!fechaSeleccionada || !horaSeleccionada) {
      toast.error('Seleccioná fecha y hora');
      return;
    }
    
    setSubmitting(true);
    try {
      console.log('Enviando reserva...', { fecha: fechaSeleccionada, hora: horaSeleccionada });
      const response = await axios.post(`${API_URL}/reservas-espacio/reservar`, {
        fecha: fechaSeleccionada,
        hora: horaSeleccionada,
        motivo: motivo.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Respuesta:', response.data);
      
      toast.success(config?.requiere_aprobacion 
        ? 'Reserva registrada. Pendiente de aprobación.'
        : '¡Reserva confirmada!'
      );
      
      setFechaSeleccionada(null);
      setHoraSeleccionada(null);
      setMotivo('');
      fetchData();
    } catch (err) {
      console.error('Error en reserva:', err);
      toast.error(err.response?.data?.detail || 'Error al reservar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelar = async (reservaId) => {
    if (!window.confirm('¿Estás seguro de cancelar esta reserva?')) return;
    
    try {
      await axios.delete(`${API_URL}/reservas-espacio/mis-reservas/${reservaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reserva cancelada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cancelar');
    }
  };

  // Generar días del mes para el calendario
  const generarDiasMes = () => {
    const primerDia = new Date(mesActual.getFullYear(), mesActual.getMonth(), 1);
    const ultimoDia = new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 0);
    const diasEnMes = ultimoDia.getDate();
    const primerDiaSemana = primerDia.getDay() === 0 ? 6 : primerDia.getDay() - 1; // Ajustar para que Lunes sea 0
    
    const dias = [];
    
    // Días vacíos al inicio
    for (let i = 0; i < primerDiaSemana; i++) {
      dias.push({ numero: null, fecha: null });
    }
    
    // Días del mes
    const hoy = new Date().toISOString().split('T')[0];
    for (let i = 1; i <= diasEnMes; i++) {
      const fecha = `${mesActual.getFullYear()}-${String(mesActual.getMonth() + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      const diaInfo = disponibilidad.find(d => d.fecha === fecha);
      const slotsDisponibles = diaInfo?.slots?.filter(s => s.disponible)?.length || 0;
      const esHoy = fecha === hoy;
      const esPasado = fecha < hoy;
      
      dias.push({
        numero: i,
        fecha,
        slotsDisponibles,
        tieneSlots: diaInfo && diaInfo.slots.length > 0,
        esHoy,
        esPasado
      });
    }
    
    return dias;
  };

  const formatearFecha = (fecha) => {
    try {
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return fecha;
    }
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      pendiente: 'bg-amber-100 text-amber-800',
      aprobada: 'bg-green-100 text-green-800',
      rechazada: 'bg-red-100 text-red-800',
      cancelada: 'bg-gray-100 text-gray-600'
    };
    const labels = {
      pendiente: 'Pendiente',
      aprobada: 'Aprobada',
      rechazada: 'Rechazada',
      cancelada: 'Cancelada'
    };
    return <Badge className={estilos[estado]}>{labels[estado]}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "min-h-screen bg-gray-50 py-8 px-4"}>
      <div className={embedded ? "" : "max-w-5xl mx-auto"}>
        {/* Header con imagen (si fue configurada). Se oculta cuando está embebido dentro de Biblioteca
            porque la Biblioteca ya muestra el mismo banner arriba, común a las 3 pestañas. */}
        {!embedded && config?.header_imagen_url && (
          <div
            className="w-full mb-6 rounded-xl overflow-hidden bg-gray-200 shadow-sm"
            style={{ aspectRatio: '3 / 1' }}
            data-testid="reservas-header-banner"
          >
            <img
              src={config.header_imagen_url}
              alt={config?.nombre || 'Espacio'}
              className="w-full h-full object-cover"
              style={{
                objectPosition: `${config?.header_position_x ?? 50}% ${config?.header_position_y ?? 50}%`,
              }}
            />
          </div>
        )}

        {/* Header — se oculta cuando se renderea embebido dentro de Biblioteca */}
        {!embedded && (
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{config?.nombre || 'Reservar Espacio'}</h1>
              <p className="text-gray-600">{config?.descripcion}</p>
            </div>
          </div>
        )}

        {/* Banner de reservas cerradas */}
        {!reservasAbiertas && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Reservas cerradas momentáneamente</p>
                  <p className="text-sm text-amber-700">El sistema de reservas no está disponible en este momento. Podés ver tus reservas existentes.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info del espacio */}
        {config && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>Turnos de {config.duracion_turno_minutos} minutos</span>
                </div>
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-600" />
                  <span>Máx. {config.max_reservas_activas} reservas activas</span>
                </div>
                {config.requiere_aprobacion && (
                  <Badge variant="outline" className="border-blue-300 text-blue-700">
                    Requiere aprobación
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={vista === 'calendario' ? 'default' : 'outline'}
            onClick={() => setVista('calendario')}
            disabled={!reservasAbiertas}
            data-testid="tab-calendario"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Reservar
          </Button>
          <Button
            variant={vista === 'mis-reservas' ? 'default' : 'outline'}
            onClick={() => setVista('mis-reservas')}
            data-testid="tab-mis-reservas"
          >
            Mis Reservas
            {misReservas.futuras.length > 0 && (
              <Badge className="ml-2 bg-primary">{misReservas.futuras.length}</Badge>
            )}
          </Button>
        </div>

        {vista === 'calendario' && reservasAbiertas ? (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Calendario */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="icon" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1))}>
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <CardTitle className="text-lg">
                    {MESES[mesActual.getMonth()]} {mesActual.getFullYear()}
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1))}>
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Encabezado días de la semana */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DIAS_SEMANA.map(dia => (
                    <div key={dia} className="text-center text-xs font-medium text-gray-500 py-2">
                      {dia}
                    </div>
                  ))}
                </div>
                
                {/* Días del mes */}
                <div className="grid grid-cols-7 gap-1">
                  {generarDiasMes().map((dia, idx) => (
                    <button
                      key={idx}
                      disabled={!dia.numero || dia.esPasado || !dia.tieneSlots || dia.slotsDisponibles === 0}
                      onClick={() => dia.fecha && handleFechaClick(dia.fecha)}
                      className={`
                        aspect-square p-1 rounded-lg text-sm relative
                        ${!dia.numero ? 'invisible' : ''}
                        ${dia.esPasado ? 'text-gray-300 cursor-not-allowed' : ''}
                        ${dia.esHoy ? 'ring-2 ring-primary' : ''}
                        ${fechaSeleccionada === dia.fecha ? 'bg-primary text-white' : ''}
                        ${!dia.esPasado && dia.tieneSlots && dia.slotsDisponibles > 0 && fechaSeleccionada !== dia.fecha ? 'hover:bg-gray-100 cursor-pointer' : ''}
                        ${!dia.tieneSlots || dia.slotsDisponibles === 0 ? 'text-gray-300' : ''}
                      `}
                      data-testid={`dia-${dia.fecha}`}
                    >
                      {dia.numero}
                      {dia.slotsDisponibles > 0 && !dia.esPasado && (
                        <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${fechaSeleccionada === dia.fecha ? 'bg-white' : 'bg-green-500'}`} />
                      )}
                    </button>
                  ))}
                </div>
                
                <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    Disponible
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-gray-300" />
                    Sin turnos
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Panel de horarios y confirmación */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {fechaSeleccionada ? `Horarios - ${formatearFecha(fechaSeleccionada)}` : 'Seleccioná una fecha'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {fechaSeleccionada ? (
                  <>
                    {slotsDelDia.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {slotsDelDia.map((slot, idx) => (
                          <button
                            key={idx}
                            disabled={!slot.disponible}
                            onClick={() => setHoraSeleccionada(slot.hora)}
                            className={`
                              py-2 px-3 rounded-lg text-sm font-medium transition-colors
                              ${!slot.disponible ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through' : ''}
                              ${slot.disponible && horaSeleccionada !== slot.hora ? 'bg-green-50 text-green-700 hover:bg-green-100' : ''}
                              ${horaSeleccionada === slot.hora ? 'bg-primary text-white' : ''}
                            `}
                            data-testid={`slot-${slot.hora}`}
                          >
                            {slot.hora}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No hay horarios disponibles</p>
                    )}

                    {horaSeleccionada && (
                      <div className="space-y-4 mt-4 pt-4 border-t">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Motivo de la reserva (opcional)
                          </label>
                          <Textarea
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Ej: Atención de paciente, reunión de equipo..."
                            rows={2}
                            data-testid="motivo-input"
                          />
                        </div>
                        
                        <div className="bg-gray-50 p-3 rounded-lg text-sm">
                          <p className="font-medium">Resumen de tu reserva:</p>
                          <p className="text-gray-600">
                            📅 {formatearFecha(fechaSeleccionada)} - 🕐 {horaSeleccionada}hs
                          </p>
                        </div>
                        
                        <Button 
                          className="w-full" 
                          onClick={handleReservar}
                          disabled={submitting}
                          data-testid="btn-confirmar-reserva"
                        >
                          {submitting ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Confirmar Reserva
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Seleccioná un día en el calendario para ver los horarios disponibles</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Vista Mis Reservas */
          <div className="space-y-6">
            {/* Reservas futuras */}
            <Card>
              <CardHeader>
                <CardTitle>Próximas reservas</CardTitle>
              </CardHeader>
              <CardContent>
                {misReservas.futuras.length > 0 ? (
                  <div className="space-y-3">
                    {misReservas.futuras.map(reserva => (
                      <div 
                        key={reserva.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        data-testid={`reserva-${reserva.id}`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{formatearFecha(reserva.fecha)}</span>
                            <span className="text-gray-500">{reserva.hora}hs</span>
                            {getEstadoBadge(reserva.estado)}
                          </div>
                          {reserva.motivo && (
                            <p className="text-sm text-gray-500 mt-1">{reserva.motivo}</p>
                          )}
                          {reserva.motivo_rechazo && (
                            <p className="text-sm text-red-600 mt-1">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              {reserva.motivo_rechazo}
                            </p>
                          )}
                        </div>
                        {reserva.cancelable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleCancelar(reserva.id)}
                            data-testid={`cancelar-${reserva.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancelar
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No tenés reservas próximas</p>
                )}
              </CardContent>
            </Card>

            {/* Historial */}
            {misReservas.pasadas.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-gray-600">Historial</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {misReservas.pasadas.map(reserva => (
                      <div 
                        key={reserva.id}
                        className="flex items-center gap-3 p-2 text-sm text-gray-500"
                      >
                        <span>{formatearFecha(reserva.fecha)}</span>
                        <span>{reserva.hora}hs</span>
                        {getEstadoBadge(reserva.estado)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
